import type { DailyLog, DayProgress, HealthMetric, QuestRank } from "./types.js";

/** 步數達標門檻。由**數值**判定——不論那個數值是同步進來的還是手動補登的，
 *  都沒有任何路徑可以直接把這一項標成完成。 */
export const STEPS_GOAL = 8000;

/** 手動打勾的欄位。步數不在其中——它是自動任務。 */
export const MANUAL_FIELDS = [
  "workout_done",
  "protein_breakfast_done",
  "protein_lunch_done",
  "protein_dinner_done",
  "vegetables_done",
  "water_done",
  "supplements_done"
] as const;

export function proteinMealCount(log: Partial<DailyLog> = {}): number {
  return [log.protein_breakfast_done, log.protein_lunch_done, log.protein_dinner_done]
    .filter(Boolean).length;
}

/** 三餐都要有主蛋白才算完成，整體只貢獻一項核心任務。 */
export function proteinDayDone(log: Partial<DailyLog> = {}): boolean {
  return proteinMealCount(log) === 3;
}

export function stepsDayDone(metric: Partial<HealthMetric> = {}): boolean {
  return Number.isFinite(metric.steps_count) && (metric.steps_count as number) >= STEPS_GOAL;
}

/**
 * 五項核心任務的完成度。
 *
 * 保健品（supplements_done）刻意不在其中——它只是提醒，不影響完成度與 Rank。
 */
export function dayCompletion(
  log: Partial<DailyLog> = {},
  metric: Partial<HealthMetric> = {}
): DayProgress {
  const tasks = [
    !!log.workout_done,
    proteinDayDone(log),
    !!log.vegetables_done,
    stepsDayDone(metric),
    !!log.water_done
  ];
  const done = tasks.filter(Boolean).length;
  return {
    done,
    total: tasks.length,
    percent: Math.round(done / tasks.length * 100),
    complete: done === tasks.length
  };
}

/** 連勝門檻：單日完成至少這麼多項核心任務（等同 A Rank 以上）。 */
export const STREAK_THRESHOLD = 4;

export function questRank(progress: Pick<DayProgress, "percent">): QuestRank {
  if (progress.percent >= 100) {
    return { grade: "S", label: "完美通關", cls: "rank-s", message: "今天的核心任務全部完成，EXP 最大化！" };
  }
  if (progress.percent >= 80) {
    return { grade: "A", label: "漂亮過關", cls: "rank-a", message: "只差一點點，今天已經非常穩。" };
  }
  if (progress.percent >= 60) {
    return { grade: "B", label: "持續推進", cls: "rank-b", message: "完成大部分任務，補上最容易的一項吧。" };
  }
  if (progress.percent >= 40) {
    return { grade: "C", label: "尚在戰鬥", cls: "rank-c", message: "先完成今天最重要的訓練或蛋白質。" };
  }
  return { grade: "D", label: "等待出發", cls: "rank-d", message: "不用一次全做，先勾掉一個最簡單的任務。" };
}
